/**
 * Click something and be told what it is.
 *
 * Two sources, because the map carries two kinds of thing. Our own placed
 * tracts and course lines are vector features whose properties are already
 * the plugin's attribute schema, so identifying one is a local query and the
 * answer is the same table QGIS would show. The PLSS grid is a raster image
 * from the BLM service, which has no features in the browser at all, so that
 * one asks the service.
 *
 * The PLSS answer is the useful half. A tract that recites "Section 6, T17S
 * R18E" and lands somewhere the grid calls Section 31 is misplaced, and no
 * amount of closure arithmetic would ever say so.
 */

const BLM_IDENTIFY =
  "https://gis.blm.gov/arcgis/rest/services/Cadastral/BLM_Natl_PLSS_CadNSDI/MapServer/identify";

export interface IdentifyRow {
  label: string;
  value: string;
}

export interface IdentifyResult {
  title: string;
  rows: IdentifyRow[];
}

/** Fields worth showing from the fabric, in the order a surveyor reads them.
 *  The service returns dozens; the rest are provenance for the fabric itself
 *  rather than anything about this parcel. */
const PLSS_FIELDS: [string, string][] = [
  ["PLSSID", "PLSS id"],
  ["TWNSHPLAB", "Township"],
  ["FRSTDIVLAB", "Section"],
  ["FRSTDIVNO", "Section number"],
  ["PRINMERCD", "Meridian"],
  ["STATEABBR", "State"],
];

/**
 * Ask the BLM fabric what covers a point.
 *
 * `identify` wants the map extent and pixel size so it can scale its
 * tolerance, which is why the caller passes both rather than just the click.
 */
export async function identifyPlss(
  lngLat: { lng: number; lat: number },
  bounds: [number, number, number, number],
  size: [number, number],
): Promise<IdentifyResult[]> {
  const params = new URLSearchParams({
    geometry: JSON.stringify({ x: lngLat.lng, y: lngLat.lat }),
    geometryType: "esriGeometryPoint",
    sr: "4326",
    layers: "all",
    tolerance: "3",
    mapExtent: bounds.join(","),
    imageDisplay: `${Math.round(size[0])},${Math.round(size[1])},96`,
    returnGeometry: "false",
    f: "json",
  });
  const response = await fetch(`${BLM_IDENTIFY}?${params.toString()}`);
  if (!response.ok)
    throw new Error(`The PLSS service answered ${response.status}.`);
  const body = (await response.json()) as {
    results?: { layerName: string; attributes: Record<string, string> }[];
  };
  return (body.results ?? [])
    .map((result) => ({
      title: result.layerName,
      rows: PLSS_FIELDS.filter(
        ([key]) => result.attributes[key] && result.attributes[key] !== "Null",
      ).map(([key, label]) => ({ label, value: result.attributes[key] })),
    }))
    .filter((result) => result.rows.length);
}

/** A placed tract's own attributes, which are the plugin's columns. */
export function describeFeature(
  properties: Record<string, unknown>,
): IdentifyResult {
  const skip = new Set(["source_doc"]);
  return {
    title: String(properties.tract ?? properties.call_no ?? "Feature"),
    rows: Object.entries(properties)
      .filter(
        ([key, value]) =>
          !skip.has(key) && value !== null && value !== "" && key !== "tract",
      )
      .map(([key, value]) => ({
        label: key.replace(/_/g, " "),
        value: String(value),
      })),
  };
}
